import { XIcon } from "lucide-react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserOption } from "@/features/session/types/user-option";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { EventOrganiser } from "./event-organiser";

type AddAttendeeModalTableProps = {
  participants: UserOption[];
  setParticipants: (participants: UserOption[]) => void;
};

export function AddAttendeeModalTable({
  participants,
  setParticipants,
}: AddAttendeeModalTableProps) {
  const columnHelper = createColumnHelper<UserOption>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        header: "User",
        cell: ({ row }) => {
          return (
            <EventOrganiser
              firstName={row.original.firstName ?? ""}
              lastName={row.original.lastName ?? ""}
              showHostedBy={false}
            />
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => {
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() =>
                  setParticipants(
                    participants.filter(
                      (participant) =>
                        participant.userId !== row.original.userId,
                    ),
                  )
                }
              >
                <XIcon className="size-4" aria-hidden={true} />
              </Button>
            </div>
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: participants ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (participants.length === 0) {
    return null;
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
